import { capitalize, Skeleton, Tooltip } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import DrawerTitle from "src/Components/Drawer/DrawerTitle";
import {
  AllTabType,
  IHomecareAppointDetail,
  IHomeCareProductDocument,
} from "src/types/GlobalInterfaces";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BloodIcon from "src/assets/images/website-images/blood-icon.png";
import {
  faChevronLeft,
  faChevronRight,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import {
  appointmentSlots,
  CURRENCY,
  generateDynamicSlots,
  IOrganizedSlots,
  IDynamicSlot,
  ImageBaseUrl,
  isTimeSlotPassed,
  TimeConvertorToLable,
} from "src/lib/constans";
import GradientBtn from "src/Components/WebsiteComponents/GradientBtn";
import { convertToKebabCase, getInitialsImage } from "src/helpers/helperFunctions";

interface IProps {
  data: IHomeCareProductDocument;
  datesRange: { date: string; count: number }[];
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  loading: boolean;
  selectedSlot: IDynamicSlot | null;
  setSelectedSlot: (slot: IDynamicSlot | null) => void;
  onBack: () => void;
  setActiveTab: (tab: AllTabType) => void;
  isEdit?: boolean;
  editData?: IHomecareAppointDetail | null;
}

const HomeCareAppSection = ({
  data,
  datesRange,
  selectedDate,
  setSelectedDate,
  loading,
  selectedSlot,
  setSelectedSlot,
  onBack,
  setActiveTab,
  isEdit,
  editData,
}: IProps) => {
  // Helper function to find time from label
  const findTimeFromLabel = (label: string): string => {
    const allSlots = generateDynamicSlots(1, selectedDate);
    const allSlotsArray = [
      ...allSlots.morning,
      ...allSlots.afternoon,
      ...allSlots.evening,
      ...allSlots.night,
    ];

    const foundSlot = allSlotsArray.find((slot) => slot.label === label);
    return foundSlot ? foundSlot.time : label;
  };

  const [allSlots, setAllSlots] = useState<IOrganizedSlots>({
    morning: [],
    afternoon: [],
    evening: [],
    night: [],
  });
  const handleDateChange = (direction: "Next" | "Previous") => {
    const currentIndex = datesRange.findIndex(
      (date) => date.date === selectedDate
    );
    let newIndex;

    if (direction === "Next") {
      newIndex = currentIndex + 1;
      if (newIndex >= datesRange.length || datesRange[newIndex].count === 0) {
        return;
      }
    } else {
      newIndex = currentIndex - 1;
      if (newIndex < 0) {
        return;
      }
    }

    setSelectedDate(datesRange[newIndex].date);
  };

  useEffect(() => {
    if (selectedDate) {
      let ele = document?.querySelector(".active-day-indicator");
      ele?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  }, [selectedDate]);

  // Function to generate and update slots
  const generateAndUpdateSlots = () => {
    if (data && selectedDate) {
      let duration = data?.serviceDuration
        ? parseInt(data?.serviceDuration)
        : 1;
      let slots = generateDynamicSlots(duration, selectedDate , editData);
      return slots as IOrganizedSlots;
    }
  };

  useEffect(() => {
    let interval: any = null;
    if (data && selectedDate) {
      // Generate slots immediately
      let slots = generateAndUpdateSlots();
      setAllSlots(slots as IOrganizedSlots);

      // Set up interval to update slots every 5 seconds
      interval = setInterval(() => {
        let slots = generateAndUpdateSlots();
        setAllSlots(slots as IOrganizedSlots);
      }, 5000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [data, selectedDate]);

  const handleResheduleAppointment = () => {
    console.log("handleResheduleAppointment");
    alert("Reshedule Appointment is not available yet");
  };

  return (
    <>
      <DrawerTitle
        title="Book Homecare Service"
        backBtn
        onBack={() => {
          onBack();
        }}
      />
      <div className="2xl:p-4 p-2 gap-4">
        <div className="w-full flex gap-2 p-2 box-design mb-3">
          <div
            className={`2xl:w-[6vw] w-[70px] aspect-square rounded-[5px] overflow-hidden ${
              data?.image ? "" : "bg-image-loader"
            }`}
          >
            {data?.image && (
              <img
                src={data?.image}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = getInitialsImage(
                    data?.categoryName?.join(" "),
                    60,
                    35,
                    "#00443f"
                  );
                }}
              />
            )}
          </div>
          <div className="flex flex-col justify-between">
            {data?.categoryName && (
              <p className="font-Poppins-Regular mt-[-2px] font-size-15px text-[#004540]">
                {data?.categoryName}
              </p>
            )}
            {data?.serviceName && (
              <p className="font-Poppins-Medium mt-[-2px] font-size-18px text-[#272727]">
                {data?.serviceName}
              </p>
            )}
            <div className="flex gap-1 items-center">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, index) => (
                  <FontAwesomeIcon
                    icon={faStar}
                    className={`${
                      data?.averageRating > index
                        ? "text-[#facc15] font-size-17px"
                        : "text-[#b7b6b1] font-size-17px"
                    }`}
                  />
                ))}
              </div>
              <p className="font-Poppins-Regular font-size-16px mb-[-2px] text-[#707070]">
                {parseFloat(data?.averageRating?.toString() || "0")?.toFixed(1)}
                {" - "}
                {data?.totalReviews || 0}{" "}
                {data?.totalReviews && data?.totalReviews > 1
                  ? "Reviews"
                  : "Review"}
              </p>
            </div>
            <div className="flex gap-1 items-center mt-1">
              <p className="font-Poppins-Regular font-size-15px text-[#707070]">
                {data?.healthCareProvider}
              </p>
            </div>
          </div>
        </div>
        <div className="w-full flex flex-col gap-2 mb-4">
          <h2 className="font-Poppins-Medium font-size-18px text-[#2F2F31]">
            Please Select Date
          </h2>
          <div className="w-full flex gap-3 justify-between items-center">
            {loading ? (
              <Skeleton variant="text" width="30%" height={20} />
            ) : (
              <p className="font-Poppins-Medium font-size-15px text-[#2F2F31]">
                {selectedDate
                  ? moment(selectedDate).format("MMM YYYY")
                  : "No date selected"}
              </p>
            )}
            <div className="flex gap-2">
              {["Previous", "Next"].map((direction) => (
                <button
                  key={direction}
                  onClick={() =>
                    handleDateChange(direction as "Next" | "Previous")
                  }
                  id={`home-care-app-section-${convertToKebabCase(
                    direction || ""
                  )}-btn`}
                  className="w-[20px] h-[20px] flex items-center justify-center rounded border border-[#00443F]"
                >
                  <FontAwesomeIcon
                    icon={direction === "Next" ? faChevronRight : faChevronLeft}
                    className="text-[#00443F] font-size-13px"
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="w-full flex">
            <div className="w-fit flex gap-2 overflow-x-auto scroll-hide">
              {loading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      width={60}
                      height={80}
                      className="rounded-xl"
                    />
                  ))
                : datesRange.map((day, index) => (
                    <button
                      id={`home-care-app-section-btn-${index + 1}`}
                      key={day.date}
                      onClick={() => setSelectedDate(day.date)}
                      className={`${
                        day.date === selectedDate
                          ? "theme-gradient-green-bottom border-transparent active-day-indicator"
                          : "bg-[#F2F8FD] border-[#E6E6E6]"
                      } py-1.5 xl:py-2 px-2 xl:px-3 rounded-xl border hover:shadow-sm transition-all duration-200 ease-in-out active:scale-95`}
                    >
                      <p
                        className={`font-Poppins-Regular ${
                          day.date === selectedDate
                            ? "text-white"
                            : "text-[#5B5B5B]"
                        } font-size-13px`}
                      >
                        {moment(day.date).format("ddd")}
                      </p>
                      <p
                        className={`font-Poppins-Medium ${
                          day.date === selectedDate
                            ? "text-white"
                            : "text-[#2F2F31]"
                        } font-size-17px`}
                      >
                        {moment(day.date).format("DD")}
                      </p>
                      <p
                        className={`font-Poppins-Regular ${
                          day.date === selectedDate
                            ? "text-white"
                            : "text-[#5B5B5B]"
                        } font-size-11px whitespace-nowrap`}
                      >
                        {day.count.toString().padStart(2, "0")} Slots
                      </p>
                    </button>
                  ))}
            </div>
          </div>
        </div>
        {selectedDate && (
          <div className="w-full flex flex-col gap-2">
            <h2 className="font-Poppins-Medium font-size-18px text-[#2F2F31]">
              {moment(selectedDate).format("dddd, MMMM DD, YYYY")}
            </h2>
            <p className="font-Poppins-Regular font-size-15px text-[#5B5B5B]">
              Time Slots Are In Pakistan Standard Time (UTC +5)
            </p>
            <div className="flex flex-col gap-2">
              {Object.entries(allSlots).map(
                ([key, value]: [string, IDynamicSlot[]]) =>
                  value.length > 0 ? (
                    <div className="w-full flex flex-col gap-2 mt-3">
                      <h3 className="font-Poppins-Medium lowercase first-letter:capitalize font-size-15px text-[#2F2F31]">
                        {capitalize(key)}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3  xl:gap-[0.5vw] gap-1">
                        {value.map((item: IDynamicSlot) => (
                          <Tooltip
                            title={
                              item.isPassed
                                ? "Time slot passed"
                                : item.afterOneHours
                                ? "Slot not available"
                                : ""
                            }
                            key={item.key}
                          >
                            <button
                              id={`home-care-app-section-${convertToKebabCase(
                                item.key || ""
                              )}-btn`}
                              key={item.key}
                              onClick={() => {
                                if (item.isPassed || item.afterOneHours || item.isAlreadySelected) {
                                  return;
                                }
                                setSelectedSlot(item);
                              }}
                              onDoubleClick={() => {
                                if (selectedSlot == item) {
                                  setSelectedSlot(null);
                                }
                              }}
                              disabled={
                                item.isPassed || loading || item.afterOneHours || item.isAlreadySelected
                              }
                              className={`${item.isAlreadySelected ? "opacity-80 cursor-not-allowed border-[#004540] border-2" : ""} ${
                                item.label === selectedSlot?.label || item.isAlreadySelected
                                  ? "theme-gradient-green-bottom border-transparent active-day-indicator"
                                  : "bg-white border-[#E6E6E6]"
                              } ${
                                item.isPassed || item.afterOneHours || item.isAlreadySelected
                                  ? "opacity-50 cursor-not-allowed"
                                  : "active:scale-95"
                              }  py-1 xl:py-1.5 px-1.5 xl:px-2 rounded-xl border hover:shadow-sm transition-all duration-200 ease-in-out flex items-center justify-between`}
                            >
                              <div className="flex flex-col gap-1 items-start">
                                <p
                                  className={`font-Lexend-Regular ${
                                    item.label === selectedSlot?.label || item.isAlreadySelected
                                      ? "text-white"
                                      : "text-[#5B5B5B]"
                                  } font-size-15px`}
                                >
                                  {capitalize(item.period)}
                                </p>
                                <p
                                  className={`font-Lexend-Regular ${
                                    item.label === selectedSlot?.label || item.isAlreadySelected
                                      ? "text-white"
                                      : "text-[#5B5B5B]"
                                  } font-size-15px`}
                                >
                                  {item.label}
                                </p>
                              </div>
                              <div className="w-[23px] h-[23px] 2xl:w-[25px] 2xl:h-[25px]">
                                <img
                                  src={
                                    item.label === selectedSlot?.label || item.isAlreadySelected
                                      ? item?.imageActive
                                      : item?.image
                                  }
                                  alt=""
                                  className={`w-full h-full object-contain`}
                                />
                              </div>
                            </button>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <></>
                  )
              )}
            </div>
          </div>
        )}
      </div>
      <div className="min-h-[100px] w-full"></div>
      <div className="w-full px-3 py-2 absolute bottom-0 left-0 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.1)] flex gap-3 mt-5">
        <div className="flex flex-col">
          {!loading && selectedDate && selectedSlot && (
            <>
              <p className="font-Poppins-Regular text-[#5B5B5B] font-size-13px">
                {moment(selectedDate).format("dddd")}
              </p>
              <p className="font-Poppins-Medium text-[#2F2F31] font-size-17px">
                {capitalize(findTimeFromLabel(selectedSlot?.label || ""))},{" "}
                {moment(selectedDate).format("DD MMM")}
              </p>
              {data?.price && (
                <p className="font-Poppins-SemiBold text-[#2F2F31] font-size-19px">
                  {CURRENCY}{" "}
                  {parseFloat(data?.price?.toString() || "0")?.toFixed(2)}
                </p>
              )}
            </>
          )}
        </div>
        <div className="flex-1 flex items-center justify-center">
          <GradientBtn
            ButtonText={isEdit ? "Reshedule" : "Continue"}
            onPress={() => {
              if (selectedSlot && selectedDate && !loading && data?.price) {
                if(isEdit){
                  handleResheduleAppointment();
                }else{
                  setActiveTab("BookHomeCare");
                }
              }
            }}
            btnExtraStyle={{
              width: "100%",
              transition: "all 0.3s",
              opacity: selectedSlot ? 1 : 0.5,
              cursor: selectedSlot ? "pointer" : "not-allowed",
            }}
          />
        </div>
      </div>
    </>
  );
};

export default HomeCareAppSection;
